import React, { useEffect, useState } from 'react'
import styles from 'styles/layout/SimilarReportsSection.module.sass'
import SectionHeader from 'components/common/SectionHeader'
import SimilarReport from 'interface/SimilarReport';
import { getSimilarReports } from 'api/ReportApiService';
import TopicCard from 'components/cards/TopicCard';

const tag = "SimilarReportsSection"

interface SimilarReportsSectionProps {
    id: string | null;
}

/**
 * SimilarReportsSection component displays a section of similar reports based on the provided report ID.
 * 
 * This component fetches similar reports from the API and displays them in a grid layout.
 * 
 * @component
 * 
 * @example
 * import SimilarReportsSection from 'components/layout/SimilarReportsSection';
 * 
 * const App: React.FC = () => (
 *   <SimilarReportsSection id="cffa23fc-4819-43e6-aade-c1e6dd6fc774" />
 * );
 * 
 * @param {SimilarReportsSectionProps} props - The props for the component.
 * @returns {JSX.Element} The rendered SimilarReportsSection component.
 */
const SimilarReportsSection: React.FC<SimilarReportsSectionProps> = ({ id }) => {
    const [similarReports, setSimilarReports] = useState<SimilarReport[]>([])

    useEffect(() => {
        const retrieveSimilarReports = async () => {
            if (id && id !== null && id.trim() !== '') {
                try {
                    const reports = await getSimilarReports({ tag, id });
                    if (reports) {
                        setSimilarReports(reports);
                    }
                } catch (error) {
                    const message = error instanceof Error ? error.message : ''
                    console.error('Error fetching similar reports: ', message)
                }
            } 
        }
        retrieveSimilarReports(); 
    }, [id]);

    if (!similarReports) return null;

    return (
        <section className={styles["similar-reports-section"]}>
            <SectionHeader
                title='INTELLIGENTLY MATCHED REPORTS'
                subtitle='Similar reports'
            />
            <div className={styles['similar-reports-container']}>
                {similarReports.map((report) => (
                    <TopicCard
                        key={report.id}
                        id={report.id}
                        highlight={report.dataset}
                        title={report.title}
                        year={report.date ? new Date(String(report.date)).getFullYear().toString() : ''}
                        similarityPercentage={report.similarityPercentage}
                    />
                ))}
            </div>
        </section>
    )
}

export default SimilarReportsSection;